import { useEffect, useRef, useState } from "react";
import { Calendar, X } from "@phosphor-icons/react";

const PRESETS = [
  { label: "7D", days: 7 },
  { label: "14D", days: 14 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function DateRangePicker({ value, onChange }) {
  // value: { mode: 'preset'|'custom', days?, date_start?, date_stop? }
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(value?.date_start || daysAgoStr(6));
  const [stop, setStop] = useState(value?.date_stop || todayStr());
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const applyPreset = (days) => {
    onChange({ mode: "preset", days });
    setOpen(false);
  };

  const applyCustom = () => {
    onChange({ mode: "custom", date_start: start, date_stop: stop });
    setOpen(false);
  };

  const display = (() => {
    if (value?.mode === "custom") return `${value.date_start} → ${value.date_stop}`;
    return `Last ${value?.days || 7} days`;
  })();

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="date-range-button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-700 px-2.5 py-1 text-xs font-mono text-zinc-300 transition-colors"
      >
        <Calendar size={12} className="text-zinc-500" />
        {display}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-[#0A0A0A] border border-zinc-800 shadow-2xl z-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Quick Range</div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X size={12} /></button>
          </div>
          <div className="grid grid-cols-4 gap-1 mb-3">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                data-testid={`preset-${p.days}`}
                onClick={() => applyPreset(p.days)}
                className={`px-2 py-1 text-[11px] font-mono ${value?.mode === "preset" && value?.days === p.days ? "bg-orange-500 text-zinc-950" : "border border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-100"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">// Custom Range</div>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Start</label>
              <input
                data-testid="date-start-input"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                max={stop}
                className="w-full bg-[#050505] border border-zinc-800 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-orange-500 text-zinc-200"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">End</label>
              <input
                data-testid="date-stop-input"
                type="date"
                value={stop}
                onChange={(e) => setStop(e.target.value)}
                min={start}
                max={todayStr()}
                className="w-full bg-[#050505] border border-zinc-800 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-orange-500 text-zinc-200"
              />
            </div>
            <button
              data-testid="date-apply-button"
              onClick={applyCustom}
              className="w-full bg-orange-500 hover:bg-orange-600 text-zinc-950 text-xs font-semibold py-1.5 transition-colors mt-1"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
